/**
 * Route evidence on Settings → Nightshift → Models: what each route has done over the last 30
 * days, so light and default lists are chosen from outcomes rather than names.
 *
 * A plain table on the shared `.table` styles; no chart, so no palette decisions beyond the
 * existing text tokens. Its own request, so a failing read leaves the route editors usable.
 */
import { useEffect, useMemo, useState } from 'react';

import type { UiModelEvidence } from '@nightshift/ui-types';

import { Kicker, Spinner } from '../components/primitives.tsx';
import { usd } from '../lib/format.ts';
import { evidenceLines, percent, type EvidenceStage } from '../lib/model-evidence.ts';
import { harnessLabel } from '../lib/route-list.ts';
import { api } from '../live/api.ts';

const WINDOW_DAYS = 30;

const STAGES: ReadonlyArray<{ value: EvidenceStage; label: string }> = [
  { value: 'Implementing', label: 'Implementing' },
  { value: 'AI Review', label: 'AI Review' },
  { value: 'Refining', label: 'Refining' },
  { value: 'all', label: 'All stages' },
];

export function ModelEvidencePanel() {
  const [evidence, setEvidence] = useState<UiModelEvidence | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<EvidenceStage>('Implementing');

  useEffect(() => {
    let cancelled = false;
    api.modelEvidence(WINDOW_DAYS).then(
      (next) => {
        if (!cancelled) setEvidence(next);
      },
      (caught: unknown) => {
        if (!cancelled) setError(caught instanceof Error ? caught.message : String(caught));
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const lines = useMemo(() => (evidence ? evidenceLines(evidence.rows, stage) : []), [evidence, stage]);

  return (
    <section className="sheet-panel" aria-labelledby="route-evidence-heading">
      <header className="panel-head">
        <Kicker count={lines.length}>
          <span id="route-evidence-heading">Route evidence</span>
        </Kicker>
        <span className="cfg-help" style={{ marginLeft: 'auto' }}>
          Last {evidence?.windowDays ?? WINDOW_DAYS} days, every project you can see.
        </span>
        <label className="cfg-help" style={{ display: 'flex', alignItems: 'center', gap: 'var(--s2)' }}>
          <span>Stage</span>
          <select className="input" value={stage} onChange={(event) => setStage(event.target.value as EvidenceStage)}>
            {STAGES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </header>
      <div className="sheet-panel-body">
        {error ? (
          <p className="cfg-error" role="alert">
            Route evidence could not be read: {error}
          </p>
        ) : !evidence ? (
          <Spinner />
        ) : lines.length === 0 ? (
          <p className="sheet-note">No finished attempt in this stage and window.</p>
        ) : (
          <table className="table" aria-label="Route evidence">
            <thead>
              <tr>
                <th>Route</th>
                <th className="right" title="Succeeded plus Failed. Cancelled, Interrupted and Superseded attempts are not outcomes.">
                  Attempts
                </th>
                <th className="right">Success</th>
                <th
                  className="right"
                  title="Of successful implementations that reached an AI Review verdict before the next Implementing attempt, how many passed that first review."
                >
                  First-pass review
                </th>
                <th className="right" title="Mean priced cost of a successful attempt. Unpriced harnesses show a dash.">
                  Avg cost per success
                </th>
                <th className="right" title="All priced spend on the route in the window, failed attempts included.">
                  Spend
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.key}>
                  <td>
                    <span className="dim">{harnessLabel(line.harness)} · </span>
                    <span className="mono">{line.model}</span>
                    {line.thinking ? <span className="dim"> · {line.thinking}</span> : null}
                  </td>
                  <td className="right num">{line.attempts}</td>
                  <td className="right num">{percent(line.successRate)}</td>
                  <td className="right num" title={`${line.reviewed} reviewed`}>
                    {percent(line.firstPassRate)}
                    {line.reviewed > 0 ? <span className="dim"> of {line.reviewed}</span> : null}
                  </td>
                  <td className="right num">{line.avgSuccessUsd === null ? '—' : usd(line.avgSuccessUsd)}</td>
                  <td className="right num">{usd(line.spendUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
